import { type ReactNode, useRef, useState } from 'react'
import cx from 'classnames'
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  Droppable,
} from '@hello-pangea/dnd'
import { Button, RawButton } from '@/components/Button'
import { Dropdown } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { SwitchCheckbox } from '@/components/SwitchCheckbox'
import styles from './DisplayMenu.module.css'

export interface DisplayMenuOption {
  label: string
  isSelected: boolean
  onSelect: () => void
}

export interface DisplayMenuColumn {
  id: string
  header: string
  isVisible: boolean
  isLocked: boolean
  onToggle: () => void
}

export interface DisplayMenuTab {
  id: string
  name: string
  isDefault: boolean
  isShown: boolean
  /**
   * Whether it can be hidden or deleted, which the last tab shown can't.
   */
  canChange: boolean
  onToggle: () => void
  onDelete: () => void
}

/**
 * Which list the menu shows below the view settings.
 */
export type DisplayMenuSection = 'tabs' | 'columns'

export interface DisplayMenuProps {
  section: DisplayMenuSection
  onSectionChange: (section: DisplayMenuSection) => void
  /**
   * Every tab: shown ones in order, then hidden ones.
   */
  tabs: DisplayMenuTab[]
  /**
   * Defaults the viewer deleted, which they can add back.
   */
  deletedTabs: { id: string; name: string }[]
  onAddBack: (id: string) => void
  /**
   * Moves a shown tab in front of another, or to the end.
   */
  onMoveTab: (id: string, beforeId: string | null) => void
  /**
   * Asks to put the default tabs back.
   */
  onResetTabs: () => void
  /**
   * Adds an empty tab and opens its editor. `undefined` when there's no room
   * for another tab.
   */
  onNewTab?: () => void
  /**
   * `null` hides the row.
   */
  scopeOptions: DisplayMenuOption[] | null
  periodOptions: DisplayMenuOption[]
  /**
   * `null` hides the row.
   */
  archivedOptions: DisplayMenuOption[] | null
  /**
   * Every column the table can show, in table order. Locked columns come
   * first.
   */
  columns: DisplayMenuColumn[]
  /**
   * Moves a column to an index in `columns`.
   */
  onMoveColumn: (id: string, toIndex: number) => void
  onResetColumns: () => void
}

export function DisplayMenu({
  section,
  onSectionChange,
  tabs,
  deletedTabs,
  onAddBack,
  onMoveTab,
  onResetTabs,
  onNewTab,
  scopeOptions,
  periodOptions,
  archivedOptions,
  columns,
  onMoveColumn,
  onResetColumns,
}: DisplayMenuProps) {
  const shownTabs = tabs.filter((t) => t.isShown)
  const hiddenTabs = tabs.filter((t) => !t.isShown)
  const [isAddingBack, setIsAddingBack] = useState(false)
  const lockedColumns = columns.filter((c) => c.isLocked)
  const movableColumns = columns.filter((c) => !c.isLocked)

  // Controlled so outside clicks and Escape close it while clicks inside
  // (toggling several columns in a row) keep it open.
  const [open, setOpen] = useState(false)
  // Escape cancels a keyboard drag before it reaches the menu, which would
  // otherwise close too. Cleared after the event, since the drag ends first.
  const isDragging = useRef(false)

  return (
    <Dropdown
      position="bottom-end"
      isOpen={open}
      onButtonPress={() => setOpen(!open)}
      onRequestClose={() => {
        if (!isDragging.current) {
          setOpen(false)
        }
      }}
      testId="job-list-display"
      button={
        <Button
          label="Display"
          size="small"
          aria-expanded={open}
          iconLeft="sliders"
        />
      }
    >
      <div className={styles.root}>
        {scopeOptions && <SegmentRow label="Show" options={scopeOptions} />}
        <SegmentRow
          label="Period"
          hint="Current covers jobs in progress and anything submitted or created this year."
          options={periodOptions}
        />
        {archivedOptions && (
          <SegmentRow label="Archived" options={archivedOptions} />
        )}
        <div className={styles.menuDivider} />
        <span
          className={styles.sectionSwitch}
          role="tablist"
          aria-label="Arrange"
        >
          {(['tabs', 'columns'] as const).map((value) => (
            <RawButton
              key={value}
              role="tab"
              aria-selected={section === value}
              className={cx(
                styles.segmentItem,
                section === value && styles.segmentItemOn,
              )}
              onPress={() => onSectionChange(value)}
              testId={`job-list-display-${value}`}
            >
              {value === 'tabs' ? 'Tabs' : 'Columns'}
            </RawButton>
          ))}
        </span>
        {section === 'tabs' ? (
          <>
            <div className={styles.menuHeader}>
              <span className={styles.menuLabel}>Tabs</span>
              <span className={styles.menuLinks}>
                {onNewTab && (
                  <RawButton
                    className={styles.menuLink}
                    onPress={() => {
                      setOpen(false)
                      onNewTab()
                    }}
                    testId="job-list-new-tab"
                  >
                    New tab
                  </RawButton>
                )}
                {deletedTabs.length > 0 && (
                  <RawButton
                    className={styles.menuLink}
                    aria-expanded={isAddingBack}
                    onPress={() => setIsAddingBack(!isAddingBack)}
                    testId="job-list-add-back-tabs"
                  >
                    Add back
                  </RawButton>
                )}
                <RawButton
                  className={styles.menuLink}
                  onPress={() => {
                    setOpen(false)
                    onResetTabs()
                  }}
                  testId="job-list-reset-tabs"
                >
                  Reset tabs
                </RawButton>
              </span>
            </div>
            {isAddingBack && deletedTabs.length > 0 && (
              <div className={styles.addBack}>
                {deletedTabs.map((tab) => (
                  <div key={tab.id} className={styles.columnRow}>
                    <span className={styles.columnLabel}>{tab.name}</span>
                    <span className={styles.columnTag}>Default</span>
                    <RawButton
                      className={styles.addBackButton}
                      onPress={() => onAddBack(tab.id)}
                      testId={`job-list-add-back-${tab.id}`}
                    >
                      Add back
                    </RawButton>
                  </div>
                ))}
              </div>
            )}
            <FadingList>
              <DragDropContext
                onDragStart={() => {
                  isDragging.current = true
                }}
                onDragEnd={(result) => {
                  setTimeout(() => {
                    isDragging.current = false
                  })
                  if (!result.destination) {
                    return
                  }
                  const others = shownTabs.filter(
                    (t) => t.id !== result.draggableId,
                  )
                  onMoveTab(
                    result.draggableId,
                    others[result.destination.index]?.id ?? null,
                  )
                }}
              >
                <Droppable
                  droppableId="job-list-tabs"
                  renderClone={(provided, snapshot, rubric) => (
                    <TabRow
                      tab={shownTabs[rubric.source.index]}
                      provided={provided}
                      snapshot={snapshot}
                    />
                  )}
                >
                  {(droppableProvided) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                    >
                      {shownTabs.map((tab, index) => (
                        <Draggable
                          key={tab.id}
                          draggableId={tab.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <TabRow
                              tab={tab}
                              provided={provided}
                              snapshot={snapshot}
                            />
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              {hiddenTabs.map((tab) => (
                <TabRow key={tab.id} tab={tab} />
              ))}
            </FadingList>
          </>
        ) : (
          <>
            <div className={styles.menuHeader}>
              <span className={styles.menuLabel}>Columns</span>
              <RawButton
                className={styles.menuLink}
                onPress={onResetColumns}
                testId="job-list-reset-columns"
              >
                Reset columns
              </RawButton>
            </div>
            <FadingList>
              {lockedColumns.map((column) => (
                <div
                  key={column.id}
                  className={cx(styles.columnRow, styles.columnRowLocked)}
                >
                  <span className={styles.columnHandle}>
                    <span className={styles.columnGrip} />
                    <span className={styles.columnLabel}>{column.header}</span>
                  </span>
                  <span className={styles.columnMeta}>Always shown</span>
                </div>
              ))}
              <DragDropContext
                onDragStart={() => {
                  isDragging.current = true
                }}
                onDragEnd={(result) => {
                  setTimeout(() => {
                    isDragging.current = false
                  })
                  if (result.destination) {
                    onMoveColumn(
                      result.draggableId,
                      result.destination.index + lockedColumns.length,
                    )
                  }
                }}
              >
                <Droppable
                  droppableId="job-list-columns"
                  // The menu animates in with a transform, which would offset a
                  // dragged row rendered in place.
                  renderClone={(provided, snapshot, rubric) => (
                    <ColumnRow
                      column={movableColumns[rubric.source.index]}
                      provided={provided}
                      snapshot={snapshot}
                    />
                  )}
                >
                  {(droppableProvided) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                    >
                      {movableColumns.map((column, index) => (
                        <Draggable
                          key={column.id}
                          draggableId={column.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <ColumnRow
                              column={column}
                              provided={provided}
                              snapshot={snapshot}
                            />
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </FadingList>
          </>
        )}
      </div>
    </Dropdown>
  )
}

interface FadingListProps {
  children: ReactNode
}

/**
 * A list that scrolls within the menu, fading out at the bottom while there
 * is more below.
 */
function FadingList({ children }: FadingListProps) {
  const [hasMore, setHasMore] = useState(false)
  const update = (el: HTMLElement) =>
    setHasMore(el.scrollTop + el.clientHeight < el.scrollHeight - 1)

  return (
    <div
      ref={(el) => {
        if (el) {
          update(el)
        }
      }}
      className={cx(styles.columnList, hasMore && styles.columnListFaded)}
      onScroll={(e) => update(e.currentTarget)}
    >
      {children}
    </div>
  )
}

interface TabRowProps {
  tab: DisplayMenuTab
  /**
   * Missing for hidden tabs, which stay put at the bottom.
   */
  provided?: DraggableProvided
  snapshot?: DraggableStateSnapshot
}

function TabRow({ tab, provided, snapshot }: TabRowProps) {
  return (
    <div
      ref={provided?.innerRef}
      {...provided?.draggableProps}
      className={cx(
        styles.columnRow,
        snapshot?.isDragging && styles.columnRowDragging,
      )}
      data-test={`job-list-tab-row-${tab.id}`}
    >
      <span
        {...provided?.dragHandleProps}
        className={cx(
          styles.columnHandle,
          !provided && styles.columnHandleStill,
        )}
        aria-label={provided ? `Reorder ${tab.name} tab` : undefined}
      >
        <Icon
          name="drag"
          size={12}
          className={cx(styles.columnGrip, !provided && styles.columnGripOff)}
        />
        <span
          className={cx(
            styles.columnLabel,
            !tab.isShown && styles.columnLabelOff,
          )}
        >
          {tab.name}
        </span>
        {tab.isDefault && <span className={styles.columnTag}>Default</span>}
      </span>
      <SwitchCheckbox
        noText
        value={tab.isShown}
        isDisabled={!tab.canChange}
        onChange={tab.onToggle}
        aria-label={`Show ${tab.name} tab`}
        testId={`job-list-tab-${tab.id}`}
      />
      <RawButton
        className={styles.rowAction}
        aria-label={`Delete ${tab.name} tab`}
        isDisabled={!tab.canChange}
        onPress={tab.onDelete}
        testId={`job-list-tab-delete-${tab.id}`}
      >
        <Icon name="delete" size={14} />
      </RawButton>
    </div>
  )
}

interface ColumnRowProps {
  column: DisplayMenuColumn
  provided: DraggableProvided
  snapshot: DraggableStateSnapshot
}

function ColumnRow({
  column,
  provided: { innerRef, draggableProps, dragHandleProps },
  snapshot,
}: ColumnRowProps) {
  return (
    <div
      ref={innerRef}
      {...draggableProps}
      className={cx(
        styles.columnRow,
        snapshot.isDragging && styles.columnRowDragging,
      )}
    >
      {/* The switch stays outside the drag handle, so that Space toggles it
          instead of lifting the row. */}
      <span
        {...dragHandleProps}
        className={styles.columnHandle}
        aria-label={`Reorder ${column.header} column`}
      >
        <Icon name="drag" size={12} className={styles.columnGrip} />
        <span
          className={cx(
            styles.columnLabel,
            !column.isVisible && styles.columnLabelOff,
          )}
        >
          {column.header}
        </span>
      </span>
      <SwitchCheckbox
        noText
        value={column.isVisible}
        onChange={column.onToggle}
        aria-label={`Show ${column.header} column`}
        testId={`job-list-column-${column.id}`}
      />
    </div>
  )
}

interface SegmentRowProps {
  label: string
  hint?: string
  options: DisplayMenuOption[]
}

function SegmentRow({ label, hint, options }: SegmentRowProps) {
  return (
    <div className={styles.menuRow}>
      <span className={styles.menuRowLabel} title={hint}>
        {label}
      </span>
      <span className={styles.segment} role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <RawButton
            key={option.label}
            role="radio"
            aria-checked={option.isSelected}
            className={cx(
              styles.segmentItem,
              option.isSelected && styles.segmentItemOn,
            )}
            onPress={option.onSelect}
          >
            {option.label}
          </RawButton>
        ))}
      </span>
    </div>
  )
}
